import styled, { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { Text } from '@lmn/design-system';
import { lastminuteDealsGradientOnText } from '@/app/common/components/Promotions/styled';
import { shouldShowStrikeThroughPrice } from '@/app/components/hotel-detail/rooms/roomCommon';
import { StrikeThroughPrice } from '@/app/components/hotel-detail/common/StrikeThroughPrice';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { PriceTypeLabel } from '../PriceSummary';
import { useInitialParams } from '../InitialParamsProvider';

const StyledStrikeThroughPrice = styled(StrikeThroughPrice)`
  margin-right: 4px;
`;

type RoomPriceProps = {
  hasFlashSalePromo: boolean;
  mealPlan: MealPlanType;
  roomsIncludedInPrice: number;
  showPriceDifference?: boolean;
  priceDifferenceMealPlanBase?: MealPlanType;
  /**
   * Whether a my-price rate tints the figure in the my-price teal. Callers that show the price of a
   * rate the user has already committed to — the Evo trip overview — pass false: there the figure is
   * the total being booked, not one rate advertising itself against others, and tinting it reads as
   * emphasis on the number rather than as a property of the rate (MPH-935). Those callers already
   * opt out of the deal colouring the same way, by passing `hasFlashSalePromo={false}`.
   */
  showMyPriceColor?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export function RoomPrice(props: RoomPriceProps) {
  const {
    mealPlan,
    roomsIncludedInPrice,
    showPriceDifference = false,
    priceDifferenceMealPlanBase,
    hasFlashSalePromo,
    showMyPriceColor = true,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { offersData } = useOffersContext();
  const { accommodationOnly } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const initialParams = useInitialParams();
  const { showTotalPrice: showTotal, toDisplayPrice } = usePriceDisplay();

  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;
  // EVO renders the price-type label ("pp") as BodySmall bold; legacy keeps the regular weight.
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled || false;

  const nights = offersData?.nights;
  const priceType = offersData?.priceType || initialParams.priceType;
  const isMyPrice = showMyPriceColor && mealPlan.isMyPrice;

  const isLmnDeal = hasFlashSalePromo && showLastMinuteDeals;
  const isFlashSale = !showLastMinuteDeals && hasFlashSalePromo;
  const showStrikeThroughPrice = shouldShowStrikeThroughPrice(mealPlan, accommodationOnly);

  let priceLabel;

  if (showPriceDifference && !accommodationOnly) {
    const priceDifference = showTotal
      ? mealPlan.finalTotalPrice -
        ((priceDifferenceMealPlanBase?.finalTotalPrice || offersData?.cheapestFilteredMealPlan?.finalTotalPrice) ?? 0)
      : mealPlan.finalPrice -
        ((priceDifferenceMealPlanBase?.finalPrice || offersData?.cheapestFilteredMealPlan?.finalPrice) ?? 0);

    priceLabel = `${priceDifference >= 0 ? '+' : '-'} ${formatPrice({ price: Math.abs(priceDifference), localization })}`;
  } else {
    priceLabel = `${formatPrice({ price: showTotal ? mealPlan.finalTotalPrice : mealPlan.finalPrice, localization })}`;
  }

  return (
    <RoomPriceContainer {...rest}>
      {accommodationOnly ? (
        <>
          {mealPlan.strikeThroughPrice && showStrikeThroughPrice ? (
            <StyledStrikeThroughPrice>
              {formatPrice({ price: mealPlan.strikeThroughPrice, localization })}
            </StyledStrikeThroughPrice>
          ) : undefined}
          <RoomPriceLabel
            data-testid="room-price"
            data-price="regular"
            $isMyPrice={isMyPrice}
            $isFlashSale={isFlashSale}
            $isLmnDeal={isLmnDeal}
            css={css`
              font-size: 22px;
            `}
          >
            {priceLabel}
          </RoomPriceLabel>
          {roomsIncludedInPrice > 1 || (nights && nights > 1) ? (
            <RoomPriceType $isMyPrice={isMyPrice} $isFlashSale={isFlashSale} $emphasized={evolutionEnabled}>
              <PriceTypeLabel priceType={priceType} roomsIncludedInPrice={roomsIncludedInPrice} />
            </RoomPriceType>
          ) : null}
        </>
      ) : (
        <>
          {!showPriceDifference && mealPlan.strikeThroughPrice && showStrikeThroughPrice ? (
            <StyledStrikeThroughPrice>
              {formatPrice({ price: toDisplayPrice(mealPlan.strikeThroughPrice), localization })}
            </StyledStrikeThroughPrice>
          ) : undefined}
          <RoomPriceLabel
            data-testid="room-price"
            data-price="regular"
            $isMyPrice={isMyPrice}
            $isFlashSale={isFlashSale}
            $isLmnDeal={isLmnDeal}
          >
            {priceLabel}
          </RoomPriceLabel>
          <RoomPriceType $isMyPrice={isMyPrice} $isFlashSale={isFlashSale} $emphasized={evolutionEnabled}>
            {showTotal ? t('vacation.oss.cards.total') : t('vacation.select.room.price.dp.perperson.short')}
          </RoomPriceType>
        </>
      )}
    </RoomPriceContainer>
  );
}

const RoomPriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

export const RoomPriceLabel = styled(Text).attrs({ variant: 'Title3' })<{
  $isMyPrice: boolean;
  $isFlashSale: boolean;
  $isLmnDeal: boolean;
}>`
  color: ${(p) =>
    p.$isLmnDeal
      ? 'transparent'
      : p.$isMyPrice
        ? p.theme.kallisto.color.common.special.myPrice[500]
        : p.$isFlashSale
          ? p.theme.commons.colors.fsPrimary
          : p.theme.commons.colors.gray800};
  ${(p) => p.$isLmnDeal && lastminuteDealsGradientOnText};
`;

export const RoomPriceType = styled(Text).attrs<{
  $isMyPrice: boolean;
  $isFlashSale: boolean;
  $emphasized?: boolean;
}>((p) => ({ variant: 'BodySmall', weight: p.$emphasized ? 'bold' : undefined }))`
  color: ${(p) =>
    p.$isMyPrice
      ? p.theme.commons.colors.gray800
      : p.$isFlashSale
        ? p.theme.commons.colors.fsPrimary
        : p.theme.commons.colors.gray800};
  white-space: nowrap;
`;

/** Below this the saving is not worth a chip. Shared with the Evo room card's own my-price badge. */
export const MIN_MY_PRICE_DISCOUNT_PERCENTAGE = 3;

export function MyPriceDiscountPercentage(props: { discountPercentage: number | undefined; elementCss?: string }) {
  const { discountPercentage, elementCss } = props;

  if (discountPercentage && discountPercentage >= MIN_MY_PRICE_DISCOUNT_PERCENTAGE) {
    return (
      <Text
        variant="Title5"
        css={css`
          color: ${(p) => p.theme.kallisto.color.common.special.myPrice[700]};
          background: ${(p) => p.theme.kallisto.color.common.special.myPrice[100]};
          padding: 4px 8px;
          border-radius: 4px;
          ${elementCss || ''};
        `}
      >
        {`-${discountPercentage}%`}
      </Text>
    );
  }
  return null;
}
